import { DeleteOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Image, List, Modal, Row, Select, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ButtonComponent } from "../../components/button/cadastrar";
import MensagemModal from "../../components/modal/mensagem";
import { ArquivoAnexoModel } from "../../models/arquivoAnexo/arquivoAnexoModel";
import { EmpresaModel } from "../../models/empresa/empresaModel";
import { DetalhesSorteioModel } from "../../models/sorteio/detalhesSorteioModel";
import { resetArquivoAnexo, setModalAnexo, setModalDeletarArquivoAnexo } from "../../redux/slices/arquivoAnexo/arquivoAnexoSlice";
import { setIdEmpresaPermissao, setTitle } from "../../redux/slices/configuracoes/configuracoesSlice";
import { deletarArquivoAnexo, listarArquivoAnexo } from "../../services/arquivoAnexoService";
import { buscarEmpresa } from "../../services/empresaService";
import { listarSorteio } from "../../services/sorteioService";
import { pdfLogo } from "../../util/assets/pdf_logo";
import { openPdfInNewTab } from "../../util/functions/openPdf";
import { verificarPermissaoEmpresa } from "../../util/functions/verificarPermissaoEmpresa";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { toastWarning } from "../../util/toast";
import CadastrarAnexoModal from "./modals/cadastrar";
import "./style/index.scss";

dayjs.locale("pt-br");

const { Text } = Typography;

type FormProps = {
  idEmpresa: number;
  idSorteio: number;
};

const Anexos = () => {
  //estados globais
  const empresaState = useAppSelector((state) => state.empresa);
  const sorteioState = useAppSelector((state) => state.sorteio);
  const persistedState = useAppSelector((state) => state.persisted);
  const arquivoAnexoState = useAppSelector((state) => state.arquivoAnexo);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<FormProps>();
  const [idArquivoAnexo, setIdArquivoAnexo] = useState<number>(0);

  useEffect(() => {
    dispatch(setTitle("ANEXOS"));
    dispatch(buscarEmpresa(empresaState.busca));
    if (persistedState.user.empresa) {
      dispatch(listarSorteio(persistedState.user.empresa.idEmpresa.toString()));
      form.setFieldsValue({
        idEmpresa: persistedState.user.empresa.idEmpresa,
      });
    }
  }, []);

  useEffect(() => {
    if (form.getFieldValue("idEmpresa") !== undefined && sorteioState.listSorteioEmpresas.length === 0) {
      toastWarning("Nenhum sorteio encontrado");
    }
  }, [form, sorteioState.listSorteioEmpresas]);

  const onChangeEmpresa = (value: number) => {
    dispatch(setIdEmpresaPermissao(value));

    form.resetFields(["idSorteio"]);

    dispatch(resetArquivoAnexo());
    dispatch(listarSorteio(value.toString()));
  };

  const onChangeSorteio = (value: number) => {
    dispatch(listarArquivoAnexo(value));
  };

  const goCadastrar = () => {
    dispatch(setModalAnexo(true));
  };

  const closeModalArquivo = () => {
    dispatch(setModalAnexo(false));
  };

  const goDeletar = (value: number) => {
    setIdArquivoAnexo(value);
    dispatch(setModalDeletarArquivoAnexo(true));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarArquivoAnexo(false));
  };

  return (
    <Flex vertical>
      <Form layout="vertical" onFinish={goCadastrar} form={form}>
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              {!persistedState.user.empresa && (
                <Form.Item
                  name="idEmpresa"
                  label="Empresa"
                  tooltip="Somente empresas que possuem sorteios podem gerenciar anexos"
                  rules={[
                    {
                      required: true,
                      message: "Campo obrigatório",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="labelSearch"
                    placeholder="Selecione uma empresa"
                    loading={empresaState.loading}
                    onChange={onChangeEmpresa}
                    style={{ width: 300 }}
                    options={empresaState.list.map((a: EmpresaModel) => ({
                      value: a.id,
                      key: a.apelido,
                      label: (
                        <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                          <div style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</div>
                          {a.apelido}
                        </Flex>
                      ),
                      labelSearch: a.apelido,
                    }))}
                  />
                </Form.Item>
              )}
              {sorteioState.listSorteioEmpresas.length > 0 && (
                <Form.Item
                  label="Data do sorteio"
                  name="idSorteio"
                  rules={[
                    {
                      required: true,
                      message: "Campo obrigatório",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={sorteioState.loadingEmpresas}
                    onChange={onChangeSorteio}
                    options={sorteioState.listSorteioEmpresas.map((a: DetalhesSorteioModel) => ({
                      value: a.idSorteio,
                      label: dayjs(a.dataSorteio).format("DD/MM/YYYY"),
                    }))}
                  />
                </Form.Item>
              )}
            </Flex>
          </Col>
          {sorteioState.listSorteioEmpresas.length > 0 && (
            <Col span={4}>
              <Flex justify="end">
                <ButtonComponent htmlType="submit" />
              </Flex>
            </Col>
          )}
        </Row>
      </Form>
      <Row gutter={12} align="bottom">
        <Col span={24}>
          <List
            size="small"
            pagination={{ position: "bottom" }}
            dataSource={arquivoAnexoState.list.filter((a: ArquivoAnexoModel) => a.idTipoArquivoAnexo === 13)}
            loading={arquivoAnexoState.loading}
            renderItem={(item: ArquivoAnexoModel, index) => (
              <List.Item key={index} className="list_item_anexos">
                {!item.caminhoArquivo?.toLowerCase().endsWith(".pdf") && (item.caminhoArquivo?.toLowerCase().endsWith(".png") || item.caminhoArquivo?.toLowerCase().endsWith(".jpeg")) && (
                  <Image className="list_image_anexos" src={item.caminhoArquivo ? item.caminhoArquivo : ""} />
                )}
                {item.caminhoArquivo?.toLowerCase().endsWith(".pdf") && (
                  <Image
                    className="list_image_anexos_pdf"
                    preview={false}
                    src={pdfLogo}
                    onClick={() => {
                      openPdfInNewTab(item.caminhoArquivo ? item.caminhoArquivo : "");
                    }}
                  />
                )}
                {!item.caminhoArquivo?.toLowerCase().endsWith(".pdf") && !item.caminhoArquivo?.toLowerCase().endsWith(".png") && !item.caminhoArquivo?.toLowerCase().endsWith(".jpeg") && (
                  <Image
                    preview={false}
                    className="list_image_anexos_pdf"
                    onClick={() => {
                      window.open(item.caminhoArquivo ? item.caminhoArquivo : "");
                    }}
                    src={
                      "data:image/png;base64,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"
                    }
                  />
                )}
                <List.Item.Meta
                  title={
                    <div className="list_title_anexos">
                      <span
                        className="list_span_title_anexos"
                        onClick={() => {
                          window.open(item.caminhoArquivo ? item.caminhoArquivo : "");
                        }}
                      >
                        {item.nome}
                      </span>
                      <div className="list_title_descricao_anexos">{item.observacao}</div>
                    </div>
                  }
                  description={<Text className="list_span_title_descricao_anexos">{`Criado em ${dayjs(item.dataCriacao ?? "").format("DD/MM/YYYY [às] HH:mm")} por ${item.usuario ?? ""}`}</Text>}
                />
                {(persistedState.user.perfil.toLowerCase() === "admin" || persistedState.user.perfil.toLowerCase() === "auditoria") && verificarPermissaoEmpresa() && (
                  <Button onClick={() => goDeletar(item.id ?? 0)} className="list_button_anexos" icon={<DeleteOutlined />} />
                )}
              </List.Item>
            )}
          />
        </Col>
      </Row>
      <Modal title={"CADASTRAR ANEXOS"} centered width={1100} open={arquivoAnexoState.modalAnexo} onCancel={closeModalArquivo} footer={null} destroyOnHidden>
        <CadastrarAnexoModal idOrigem={form.getFieldValue("idSorteio")} />
      </Modal>
      <Modal title={"REMOVER ANEXO"} centered width={350} open={arquivoAnexoState.modalDeletar} onCancel={closeModalDeletar} footer={null} destroyOnHidden>
        <MensagemModal text="Deseja remover o anexo?" action={deletarArquivoAnexo({ value: idArquivoAnexo, busca: form.getFieldValue("idSorteio") })} close={setModalDeletarArquivoAnexo(false)} />
      </Modal>
    </Flex>
  );
};

export default Anexos;
